import type { AutoLinkBootstrapResult } from '../verify-auto-link/v2/auto-link-bootstrap-context.js';
import type {
  AccountBootstrapDecision,
  AccountZeroStudioDecision
} from './account-bootstrap-decision.js';

export type RootZeroStudioBootstrapActions<TStudio> = {
  readonly requestAutoLink: () => Promise<AutoLinkBootstrapResult>;
  readonly bootstrapDefaultStudio: () => Promise<readonly TStudio[]>;
  readonly openAgentSetup: () => void;
};

export type RootZeroStudioBootstrapResult<TStudio> =
  | { readonly kind: 'auto_link'; readonly result: AutoLinkBootstrapResult }
  | { readonly kind: 'default_studio'; readonly studios: readonly TStudio[] }
  | { readonly kind: 'setup_opened' };

class UnexpectedZeroStudioDecisionError extends Error {
  public constructor(value: never) {
    super(`Unexpected zero-Studio decision: ${JSON.stringify(value)}`);
    this.name = 'UnexpectedZeroStudioDecisionError';
  }
}

const assertNever = (value: never): never => {
  throw new UnexpectedZeroStudioDecisionError(value);
};

export const isZeroStudioDecision = <TStudio>(
  decision: AccountBootstrapDecision<TStudio>
): decision is AccountZeroStudioDecision => {
  switch (decision.kind) {
    case 'request_zero_auto_link':
    case 'bootstrap_default_studio':
    case 'open_agent_setup':
      return true;
    case 'wait_for_inventory':
    case 'wait_for_identity':
    case 'identity_error':
    case 'inventory_error':
    case 'access_empty':
    case 'enter_studio':
    case 'choose_studio':
      return false;
    default:
      return assertNever(decision);
  }
};

/** Root delegates a settled empty inventory to exactly one platform-specific action. */
export const runRootZeroStudioBootstrap = async <TStudio>(
  decision: AccountZeroStudioDecision,
  actions: RootZeroStudioBootstrapActions<TStudio>
): Promise<RootZeroStudioBootstrapResult<TStudio>> => {
  switch (decision.kind) {
    case 'request_zero_auto_link':
      return { kind: 'auto_link', result: await actions.requestAutoLink() };
    case 'bootstrap_default_studio':
      return { kind: 'default_studio', studios: await actions.bootstrapDefaultStudio() };
    case 'open_agent_setup':
      actions.openAgentSetup();
      return { kind: 'setup_opened' };
    default:
      return assertNever(decision);
  }
};
